// /app/frontend/src/pages/compliance/vendor/DueDiligenceWorkflow.jsx
// Separate Due Diligence Assessment Workflow

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Progress } from '../../../components/ui/progress';
import { Separator } from '../../../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { DatePicker } from '../../../components/ui/date-picker';
import { dueDiligenceAPI, vendorSimplifiedAPI, entitiesAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft, ArrowRight, Save, Building2, Shield, Database, Lock,
  CheckCircle, XCircle, AlertTriangle, Loader2, ClipboardCheck, Users, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { CARIBBEAN_COUNTRIES, ASSESSMENT_OUTCOMES } from './constants-simplified';

// Tab configuration
const DD_TABS = [
  { id: 'assessment-info', label: 'Assessment Info', icon: ClipboardCheck },
  { id: 'data-compliance', label: 'Data Compliance', icon: Database },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'sub-processors', label: 'Sub-Processors', icon: Users },
  { id: 'result', label: 'Result', icon: Shield },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'completed', label: 'Completed' },
];

// Initial form data
const DD_INITIAL_DATA = {
  entity_id: '',
  vendor_id: '',
  assessment_date: new Date().toISOString().split('T')[0],
  assessor_name: '',
  assessor_email: '',
  assessor_comments: '',
  personal_data_description: '',
  business_processes: '',
  data_storage_countries: [],
  data_guaranteed_in_country: null,
  special_category_data: null,
  special_category_description: '',
  encryption_in_transit: null,
  encryption_in_transit_standard: '',
  encryption_at_rest: null,
  encryption_at_rest_standard: '',
  dsr_procedures_in_place: null,
  dsr_procedures_details: '',
  data_return_on_termination: null,
  data_return_procedure: '',
  security_procedures: '',
  who_has_access: '',
  access_controls: '',
  breach_detection_procedures: '',
  audit_facilities: '',
  breach_reporting_procedures: null,
  breach_reporting_details: '',
  breaches_in_last_3_years: null,
  breach_history_details: '',
  independent_audits: null,
  last_audit_date: '',
  audit_report_available: null,
  uses_sub_processors: null,
  sub_processors_list: '',
  sub_processors_authorized_by: '',
  sub_processors_written_agreements: null,
  assessment_outcome: 'pending',
  actions_required: '',
  actions_completed: null,
  actions_completed_date: '',
  assessment_completion_date: '',
  final_comments: '',
  status: 'draft',
  reviewed_by: '',
  review_date: '',
  approved_by: '',
  approval_date: '',
};

// Yes/No Button Group
function YesNoButtons({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        size="sm"
        variant={value === true ? "default" : "outline"}
        onClick={() => onChange(true)}
        className={value === true ? "bg-green-600 hover:bg-green-700" : ""}
      >
        <CheckCircle className="h-4 w-4 mr-1" />
        Yes
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === false ? "default" : "outline"}
        onClick={() => onChange(false)}
        className={value === false ? "bg-red-600 hover:bg-red-700" : ""}
      >
        <XCircle className="h-4 w-4 mr-1" />
        No
      </Button>
    </div>
  );
}

export default function DueDiligenceWorkflow() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const vendorIdFromUrl = searchParams.get('vendor');
  const { isDPO, user } = useAuth();
  const { theme } = useTheme();
  const isEditing = !!id;

  const [formData, setFormData] = useState(DD_INITIAL_DATA);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('assessment-info');
  const [recordId, setRecordId] = useState(id || null);
  const [ddReference, setDdReference] = useState('');

  const lastSavedData = useRef(null);
  const autoSaveTimer = useRef(null);

  // Export Due Diligence to DOCX
  const handleExport = async () => {
    if (!recordId) {
      toast.error('Please save the assessment before exporting');
      return;
    }
    
    try {
      setExporting(true);
      const response = await dueDiligenceAPI.exportDocx(recordId);
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `Due_Diligence_${ddReference || 'export'}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Due Diligence report exported successfully');
    } catch (error) {
      console.error('Failed to export Due Diligence:', error);
      toast.error('Failed to export Due Diligence report');
    } finally {
      setExporting(false);
    }
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (isDPO) {
          const entitiesRes = await entitiesAPI.getAll();
          setEntities(entitiesRes.data);
        }

        // Load vendors
        const vendorsRes = await vendorSimplifiedAPI.getAll();
        setVendors(vendorsRes.data);

        if (isEditing) {
          // Load existing assessment
          const ddRes = await dueDiligenceAPI.getOne(id);
          const dd = ddRes.data;
          setFormData({ ...DD_INITIAL_DATA, ...dd });
          setDdReference(dd.assessment_reference || '');
          lastSavedData.current = JSON.stringify(dd);
          
          // Load vendor info
          if (dd.vendor_id) {
            const vendorRes = await vendorSimplifiedAPI.getOne(dd.vendor_id);
            setSelectedVendor(vendorRes.data);
          }
        } else if (vendorIdFromUrl) {
          // Pre-select vendor from URL
          const vendorRes = await vendorSimplifiedAPI.getOne(vendorIdFromUrl);
          setSelectedVendor(vendorRes.data);
          setFormData(prev => ({
            ...prev,
            vendor_id: vendorIdFromUrl,
            entity_id: vendorRes.data.entity_id,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, isDPO, vendorIdFromUrl]);

  // Auto-save
  const performAutoSave = useCallback(async () => {
    if (!formData.vendor_id) return;
    if (!formData.entity_id && isDPO) return;

    const currentData = JSON.stringify(formData);
    if (currentData === lastSavedData.current) return;

    try {
      setAutoSaveStatus('saving');

      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.entity_id,
      };

      if (recordId) {
        await dueDiligenceAPI.update(recordId, submitData);
      } else {
        const res = await dueDiligenceAPI.create(submitData);
        setRecordId(res.data.id);
        setDdReference(res.data.assessment_reference);
        window.history.replaceState(null, '', `/compliance/due-diligence/${res.data.id}`);
      }

      lastSavedData.current = currentData;
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('idle');
    }
  }, [formData, recordId, isDPO, user]);

  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(performAutoSave, 2000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, performAutoSave]);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDataCountry = (country) => {
    setFormData(prev => {
      const current = prev.data_storage_countries || [];
      if (current.includes(country)) {
        return { ...prev, data_storage_countries: current.filter(c => c !== country) };
      }
      return { ...prev, data_storage_countries: [...current, country] };
    });
  };

  // Handle vendor selection
  const handleVendorSelect = async (vendorId) => {
    if (vendorId === "select") {
      setSelectedVendor(null);
      handleChange('vendor_id', '');
      return;
    }
    
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setSelectedVendor(vendor);
      setFormData(prev => ({
        ...prev,
        vendor_id: vendorId,
        entity_id: vendor.entity_id,
      }));
    }
  };

  // Navigation
  const goToNextTab = () => {
    const currentIndex = DD_TABS.findIndex(t => t.id === activeTab);
    if (currentIndex < DD_TABS.length - 1) {
      setActiveTab(DD_TABS[currentIndex + 1].id);
    }
  };

  const goToPrevTab = () => {
    const currentIndex = DD_TABS.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(DD_TABS[currentIndex - 1].id);
    }
  };

  // Final save
  const handleSave = async (asDraft = false) => {
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.entity_id,
        status: asDraft ? 'draft' : formData.status,
      };

      if (recordId) {
        await dueDiligenceAPI.update(recordId, submitData);
        toast.success('Assessment saved successfully');
      } else {
        const res = await dueDiligenceAPI.create(submitData);
        setRecordId(res.data.id);
        toast.success('Assessment created successfully');
      }

      if (!asDraft) {
        navigate('/compliance/due-diligence');
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getOutcomeBadge = (outcome) => {
    const colors = {
      'pending': 'bg-gray-100 text-gray-700',
      'acceptable': 'bg-green-100 text-green-700',
      'requires_action': 'bg-yellow-100 text-yellow-700',
      'unacceptable': 'bg-red-100 text-red-700',
    };
    return colors[outcome] || colors['pending'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-background", theme === 'dark' ? 'dark' : '')}>
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/due-diligence')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">
                  {isEditing ? 'Edit Due Diligence Assessment' : 'New Due Diligence Assessment'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {ddReference && <Badge variant="outline" className="mr-2">{ddReference}</Badge>}
                  {selectedVendor && (
                    <span>
                      Vendor: <strong>{selectedVendor.vendor_name}</strong>
                      {selectedVendor.vendor_reference && ` (${selectedVendor.vendor_reference})`}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Auto-save indicator */}
              <div className="text-sm text-muted-foreground">
                {autoSaveStatus === 'saving' && <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving...</span>}
                {autoSaveStatus === 'saved' && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3 w-3" /> Saved</span>}
              </div>
              {/* Status */}
              <Badge variant="secondary">
                {STATUS_OPTIONS.find(s => s.value === formData.status)?.label || 'Draft'}
              </Badge>
              {/* Outcome */}
              {formData.assessment_outcome !== 'pending' && (
                <Badge className={getOutcomeBadge(formData.assessment_outcome)}>
                  {ASSESSMENT_OUTCOMES.find(o => o.value === formData.assessment_outcome)?.label}
                </Badge>
              )}
              {/* Export Button */}
              {recordId && (
                <Button 
                  variant="outline" 
                  onClick={handleExport} 
                  disabled={exporting}
                  data-testid="dd-export-btn"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Tab Navigation */}
          <TabsList className="grid grid-cols-5 h-auto p-1">
            {DD_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex flex-col gap-1 py-2 px-1 text-xs"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline text-xs">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab 1: Assessment Info */}
          <TabsContent value="assessment-info" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Vendor Selection</CardTitle>
                <CardDescription>Select the vendor to assess</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Vendor <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.vendor_id || "select"}
                    onValueChange={handleVendorSelect}
                    disabled={isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor to assess" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select">Select vendor</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.vendor_name} {v.vendor_reference && `(${v.vendor_reference})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedVendor && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm"><strong>Service:</strong> {selectedVendor.service_description || 'N/A'}</p>
                    <p className="text-sm"><strong>Country:</strong> {CARIBBEAN_COUNTRIES.find(c => c.code === selectedVendor.country_of_registration)?.name || selectedVendor.country_of_registration}</p>
                    <p className="text-sm"><strong>Status:</strong> {selectedVendor.lifecycle_stage}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Assessment Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessment Date</Label>
                    <DatePicker
                      value={formData.assessment_date?.split('T')[0] || ''}
                      onChange={(val) => handleChange('assessment_date', val)}
                      placeholder="Select assessment date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(val) => handleChange('status', val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessor Name</Label>
                    <Input
                      value={formData.assessor_name}
                      onChange={(e) => handleChange('assessor_name', e.target.value)}
                      placeholder="Who is conducting the assessment?"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Assessor Email</Label>
                    <Input
                      type="email"
                      value={formData.assessor_email}
                      onChange={(e) => handleChange('assessor_email', e.target.value)}
                      placeholder="assessor@company.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Assessor Comments</Label>
                  <Textarea
                    value={formData.assessor_comments}
                    onChange={(e) => handleChange('assessor_comments', e.target.value)}
                    placeholder="Any relevant circumstances affecting the assessment..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Data Compliance */}
          <TabsContent value="data-compliance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Data Processing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Personal Data Held/Processed <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={formData.personal_data_description}
                    onChange={(e) => handleChange('personal_data_description', e.target.value)}
                    placeholder="Describe the personal data this vendor stores/processes..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Business Processes Involved</Label>
                  <Textarea
                    value={formData.business_processes}
                    onChange={(e) => handleChange('business_processes', e.target.value)}
                    placeholder="Which business processes require this vendor?"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Is Special Category Data Involved?</Label>
                  <YesNoButtons
                    value={formData.special_category_data}
                    onChange={(val) => handleChange('special_category_data', val)}
                  />
                  {formData.special_category_data && (
                    <Textarea
                      className="mt-2"
                      value={formData.special_category_description}
                      onChange={(e) => handleChange('special_category_description', e.target.value)}
                      placeholder="Describe the special category data..."
                      rows={2}
                    />
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Countries Where Data is Stored/Processed</Label>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                    {CARIBBEAN_COUNTRIES.slice(0, 15).map(c => (
                      <label key={c.code} className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                          checked={(formData.data_storage_countries || []).includes(c.code)}
                          onCheckedChange={() => toggleDataCountry(c.code)}
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Is data guaranteed to remain in stated countries?</Label>
                  <YesNoButtons
                    value={formData.data_guaranteed_in_country}
                    onChange={(val) => handleChange('data_guaranteed_in_country', val)}
                  />
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Encrypted in Transit?</Label>
                    <YesNoButtons
                      value={formData.encryption_in_transit}
                      onChange={(val) => handleChange('encryption_in_transit', val)}
                    />
                    {formData.encryption_in_transit && (
                      <Input
                        className="mt-2"
                        value={formData.encryption_in_transit_standard}
                        onChange={(e) => handleChange('encryption_in_transit_standard', e.target.value)}
                        placeholder="Standard (e.g., TLS 1.3)"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Data Encrypted at Rest?</Label>
                    <YesNoButtons
                      value={formData.encryption_at_rest}
                      onChange={(val) => handleChange('encryption_at_rest', val)}
                    />
                    {formData.encryption_at_rest && (
                      <Input
                        className="mt-2"
                        value={formData.encryption_at_rest_standard}
                        onChange={(e) => handleChange('encryption_at_rest_standard', e.target.value)}
                        placeholder="Standard (e.g., AES-256)"
                      />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Can vendor assist with Data Subject Requests (DSRs)?</Label>
                  <YesNoButtons
                    value={formData.dsr_procedures_in_place}
                    onChange={(val) => handleChange('dsr_procedures_in_place', val)}
                  />
                  {formData.dsr_procedures_in_place && (
                    <Textarea
                      className="mt-2"
                      value={formData.dsr_procedures_details}
                      onChange={(e) => handleChange('dsr_procedures_details', e.target.value)}
                      placeholder="Describe DSR support procedures..."
                      rows={2}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Will data be returned on contract termination?</Label>
                  <YesNoButtons
                    value={formData.data_return_on_termination}
                    onChange={(val) => handleChange('data_return_on_termination', val)}
                  />
                  {formData.data_return_on_termination && (
                    <Textarea
                      className="mt-2"
                      value={formData.data_return_procedure}
                      onChange={(e) => handleChange('data_return_procedure', e.target.value)}
                      placeholder="Describe the data return procedure..."
                      rows={2}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Security */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Security Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Security Procedures</Label>
                  <Textarea
                    value={formData.security_procedures}
                    onChange={(e) => handleChange('security_procedures', e.target.value)}
                    placeholder="Describe physical, administrative, and technical security procedures..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Who has access to personal data?</Label>
                  <Textarea
                    value={formData.who_has_access}
                    onChange={(e) => handleChange('who_has_access', e.target.value)}
                    placeholder="Internal/external staff with data access..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Access Controls</Label>
                  <Textarea
                    value={formData.access_controls}
                    onChange={(e) => handleChange('access_controls', e.target.value)}
                    placeholder="Who controls and authorises access?"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Breach Detection Procedures</Label>
                  <Textarea
                    value={formData.breach_detection_procedures}
                    onChange={(e) => handleChange('breach_detection_procedures', e.target.value)}
                    placeholder="Procedures to detect unauthorized access..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Audit Facilities</Label>
                  <Textarea
                    value={formData.audit_facilities}
                    onChange={(e) => handleChange('audit_facilities', e.target.value)}
                    placeholder="Data audit mechanisms in place..."
                    rows={2}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Does vendor have breach reporting procedures?</Label>
                  <YesNoButtons
                    value={formData.breach_reporting_procedures}
                    onChange={(val) => handleChange('breach_reporting_procedures', val)}
                  />
                  {formData.breach_reporting_procedures && (
                    <Textarea
                      className="mt-2"
                      value={formData.breach_reporting_details}
                      onChange={(e) => handleChange('breach_reporting_details', e.target.value)}
                      placeholder="Describe breach reporting procedures..."
                      rows={2}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Any data breaches in the last 3 years?</Label>
                  <YesNoButtons
                    value={formData.breaches_in_last_3_years}
                    onChange={(val) => handleChange('breaches_in_last_3_years', val)}
                  />
                  {formData.breaches_in_last_3_years && (
                    <Textarea
                      className="mt-2"
                      value={formData.breach_history_details}
                      onChange={(e) => handleChange('breach_history_details', e.target.value)}
                      placeholder="Describe the breach(es) and actions taken..."
                      rows={2}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Are security controls independently audited?</Label>
                    <YesNoButtons
                      value={formData.independent_audits}
                      onChange={(val) => handleChange('independent_audits', val)}
                    />
                  </div>
                  {formData.independent_audits && (
                    <>
                      <div className="space-y-2">
                        <Label>Last Audit Date</Label>
                        <DatePicker
                          value={formData.last_audit_date?.split('T')[0] || ''}
                          onChange={(val) => handleChange('last_audit_date', val)}
                          placeholder="Select last audit date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Audit Report Available?</Label>
                        <YesNoButtons
                          value={formData.audit_report_available}
                          onChange={(val) => handleChange('audit_report_available', val)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Sub-Processors */}
          <TabsContent value="sub-processors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sub-Processors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Does vendor use sub-processors?</Label>
                  <YesNoButtons
                    value={formData.uses_sub_processors}
                    onChange={(val) => handleChange('uses_sub_processors', val)}
                  />
                </div>

                {formData.uses_sub_processors && (
                  <>
                    <div className="space-y-2">
                      <Label>List of Sub-Processors</Label>
                      <Textarea
                        value={formData.sub_processors_list}
                        onChange={(e) => handleChange('sub_processors_list', e.target.value)}
                        placeholder="List sub-processors with name, service, and location..."
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Who authorizes sub-processor use?</Label>
                      <Input
                        value={formData.sub_processors_authorized_by}
                        onChange={(e) => handleChange('sub_processors_authorized_by', e.target.value)}
                        placeholder="Name/role of authorizing person"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Are written agreements in place with sub-processors?</Label>
                      <YesNoButtons
                        value={formData.sub_processors_written_agreements}
                        onChange={(val) => handleChange('sub_processors_written_agreements', val)}
                      />
                    </div>
                  </>
                )}

                {formData.uses_sub_processors === false && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">No Sub-Processors</span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      This vendor does not use sub-processors for data processing.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 5: Result */}
          <TabsContent value="result" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  Assessment Result
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Assessment Outcome <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.assessment_outcome}
                    onValueChange={(val) => handleChange('assessment_outcome', val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSESSMENT_OUTCOMES.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.assessment_outcome === 'requires_action' && (
                  <>
                    <div className="space-y-2">
                      <Label>Actions Required</Label>
                      <Textarea
                        value={formData.actions_required}
                        onChange={(e) => handleChange('actions_required', e.target.value)}
                        placeholder="Describe required actions and who is responsible..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Actions Completed?</Label>
                        <YesNoButtons
                          value={formData.actions_completed}
                          onChange={(val) => handleChange('actions_completed', val)}
                        />
                      </div>
                      {formData.actions_completed && (
                        <div className="space-y-2">
                          <Label>Completion Date</Label>
                          <DatePicker
                            value={formData.actions_completed_date?.split('T')[0] || ''}
                            onChange={(val) => handleChange('actions_completed_date', val)}
                            placeholder="Select completion date"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Assessment Completion Date</Label>
                  <DatePicker
                    value={formData.assessment_completion_date?.split('T')[0] || ''}
                    onChange={(val) => handleChange('assessment_completion_date', val)}
                    placeholder="Select assessment completion date"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Final Comments</Label>
                  <Textarea
                    value={formData.final_comments}
                    onChange={(e) => handleChange('final_comments', e.target.value)}
                    placeholder="Any other relevant factors..."
                    rows={3}
                  />
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reviewed By</Label>
                    <Input
                      value={formData.reviewed_by}
                      onChange={(e) => handleChange('reviewed_by', e.target.value)}
                      placeholder="Name of reviewer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Review Date</Label>
                    <DatePicker
                      value={formData.review_date?.split('T')[0] || ''}
                      onChange={(val) => handleChange('review_date', val)}
                      placeholder="Select review date"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Approved By</Label>
                    <Input
                      value={formData.approved_by}
                      onChange={(e) => handleChange('approved_by', e.target.value)}
                      placeholder="Name of approver"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Approval Date</Label>
                    <DatePicker
                      value={formData.approval_date?.split('T')[0] || ''}
                      onChange={(val) => handleChange('approval_date', val)}
                      placeholder="Select approval date"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={goToPrevTab}
              disabled={activeTab === DD_TABS[0].id}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/compliance/due-diligence')}>
                Cancel
              </Button>
              <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}>
                Save Draft
              </Button>
              {activeTab === DD_TABS[DD_TABS.length - 1].id ? (
                <Button onClick={() => handleSave(false)} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save & Close'}
                </Button>
              ) : (
                <Button onClick={goToNextTab}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
